import { c as _c } from "react/compiler-runtime";
import { DropdownDivider, DropdownItem, DropdownSubmenu } from 'platform/components';
import { Icon } from 'platform/foundation-tw';
import { useColumnMenuContext } from '../../../../../../../../context/ColumnMenuContext';
import { useIsMobileView } from '../../../../../../../../hooks/useIsMobileView';
type ColumnMenuOptionsTabProps = {
  currentPinned: 'LEFT' | 'RIGHT' | null | undefined;
  /** Key of the column this menu belongs to — used to call onAutosizeColumn / onPinColumn */
  columnKey: string;
  /** Whether the column supports sorting — controls Sort submenu visibility */
  columnSortable: boolean;
  /** Called after any action to close the parent popover */
  onClose: () => void;
};
const PIN_OPTIONS: {
  label: string;
  value: 'LEFT' | 'RIGHT' | null;
}[] = [{
  label: 'No pin',
  value: null
}, {
  label: 'Pin left',
  value: 'LEFT'
}, {
  label: 'Pin right',
  value: 'RIGHT'
}];
const SORT_OPTIONS: {
  label: string;
  value: 'ASC' | 'DESC' | null;
}[] = [{
  label: 'Sort ascending',
  value: 'ASC'
}, {
  label: 'Sort descending',
  value: 'DESC'
}, {
  label: 'Clear sort',
  value: null
}];

/**
 * Options tab content for the column header menu.
 *
 * Renders: Sort submenu (if sortable), Pin column submenu (No pin / Pin left / Pin right),
 * Autosize this column, Autosize all columns, divider, Reset columns, Reset filters.
 *
 * Visibility of pinning and autosize groups is controlled via ColumnMenuContext
 * (showPinning / showAutosize flags from the backend definition).
 * All action callbacks are also read from ColumnMenuContext.
 *
 * Must be rendered inside a <Dropdown> / <DropdownContainer> so that DropdownItem
 * receives the required MenuContext + FloatingList context from platform/components.
 */
export function ColumnMenuOptionsTab(props) {
  const $ = _c(46);
  const columnMenu = useColumnMenuContext();
  const isMobile = useIsMobileView();
  const normalizedPinned = props.currentPinned ?? null;
  let t0;
  if ($[0] !== columnMenu.columnStates || $[1] !== props.columnKey) {
    t0 = columnMenu.columnStates.get(props.columnKey)?.sort ?? null;
    $[0] = columnMenu.columnStates;
    $[1] = props.columnKey;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const currentSort = t0;
  let t1;
  if ($[3] !== columnMenu || $[4] !== props) {
    t1 = sort => {
      columnMenu.onSort(props.columnKey, sort);
      props.onClose();
    };
    $[3] = columnMenu;
    $[4] = props;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const handleSort = t1;
  let t2;
  if ($[6] !== columnMenu || $[7] !== props) {
    t2 = value => {
      columnMenu.onPinColumn(props.columnKey, value);
      props.onClose();
    };
    $[6] = columnMenu;
    $[7] = props;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const handlePin = t2;
  let t3;
  if ($[9] !== columnMenu || $[10] !== props) {
    t3 = () => {
      columnMenu.onAutosizeColumn(props.columnKey);
      props.onClose();
    };
    $[9] = columnMenu;
    $[10] = props;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const handleAutosizeThis = t3;
  let t4;
  if ($[12] !== columnMenu || $[13] !== props) {
    t4 = () => {
      columnMenu.onAutosizeAllColumns();
      props.onClose();
    };
    $[12] = columnMenu;
    $[13] = props;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const handleAutosizeAll = t4;
  let t5;
  if ($[15] !== columnMenu || $[16] !== props) {
    t5 = () => {
      columnMenu.onResetColumns();
      props.onClose();
    };
    $[15] = columnMenu;
    $[16] = props;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const handleResetColumns = t5;
  let t6;
  if ($[18] !== columnMenu || $[19] !== props) {
    t6 = () => {
      columnMenu.onResetFilters();
      props.onClose();
    };
    $[18] = columnMenu;
    $[19] = props;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const handleResetFilters = t6;
  let t7;
  if ($[21] !== currentSort || $[22] !== handleSort || $[23] !== isMobile || $[24] !== props.columnSortable) {
    t7 = props.columnSortable && (isMobile ? <>{SORT_OPTIONS.map(option => <DropdownItem key={option.value ?? "none"} label={option.label} isActive={currentSort === option.value && option.value !== null} isDisabled={option.value === null && currentSort === null} onClick={() => handleSort(option.value)} data-testid={`columnMenuSortOption-${option.value ?? "none"}`} />)}<DropdownDivider /></> : <DropdownSubmenu label="Sort" leftIcon="content/sort" isActive={currentSort !== null} suffix={<Icon value="navigation/arrow_right" />} placement="right-start" strategy="fixed" data-testid="columnMenuSortSubmenu">{SORT_OPTIONS.map(option_0 => <DropdownItem key={option_0.value ?? "none"} label={option_0.label} isActive={currentSort === option_0.value && option_0.value !== null} isDisabled={option_0.value === null && currentSort === null} onClick={() => handleSort(option_0.value)} data-testid={`columnMenuSortOption-${option_0.value ?? "none"}`} />)}</DropdownSubmenu>);
    $[21] = currentSort;
    $[22] = handleSort;
    $[23] = isMobile;
    $[24] = props.columnSortable;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== columnMenu.showPinning || $[27] !== handlePin || $[28] !== isMobile || $[29] !== normalizedPinned) {
    t8 = columnMenu.showPinning && (isMobile ? <>{PIN_OPTIONS.map(option_1 => <DropdownItem key={option_1.value ?? "none"} label={option_1.label} isActive={normalizedPinned === option_1.value} onClick={() => handlePin(option_1.value)} data-testid={`columnMenuPinOption-${option_1.value ?? "none"}`} />)}<DropdownDivider /></> : <DropdownSubmenu label="Pin column" leftIcon="custom/push_pin" isActive={normalizedPinned !== null} suffix={<Icon value="navigation/arrow_right" />} placement="right-start" strategy="fixed" data-testid="columnMenuPinSubmenu">{PIN_OPTIONS.map(option_2 => <DropdownItem key={option_2.value ?? "none"} label={option_2.label} isActive={normalizedPinned === option_2.value} onClick={() => handlePin(option_2.value)} data-testid={`columnMenuPinOption-${option_2.value ?? "none"}`} />)}</DropdownSubmenu>);
    $[26] = columnMenu.showPinning;
    $[27] = handlePin;
    $[28] = isMobile;
    $[29] = normalizedPinned;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  let t9;
  if ($[31] !== columnMenu.showAutosize || $[32] !== handleAutosizeAll || $[33] !== handleAutosizeThis) {
    t9 = columnMenu.showAutosize && <><DropdownItem label="Autosize this column" leftIcon="action/aspect_ratio" onClick={handleAutosizeThis} data-testid="columnMenuAutosizeThis" /><DropdownItem label="Autosize all columns" leftIcon="action/aspect_ratio" onClick={handleAutosizeAll} data-testid="columnMenuAutosizeAll" /></>;
    $[31] = columnMenu.showAutosize;
    $[32] = handleAutosizeAll;
    $[33] = handleAutosizeThis;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  let t10;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <DropdownDivider data-testid="columnMenuDivider" />;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  let t11;
  if ($[36] !== handleResetColumns) {
    t11 = <DropdownItem label="Reset columns" leftIcon="action/restore" onClick={handleResetColumns} data-testid="columnMenuResetColumns" />;
    $[36] = handleResetColumns;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  let t12;
  if ($[38] !== handleResetFilters) {
    t12 = <DropdownItem label="Reset filters" leftIcon="content/filter_list" onClick={handleResetFilters} data-testid="columnMenuResetFilters" />;
    $[38] = handleResetFilters;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  let t13;
  if ($[40] !== t11 || $[41] !== t12 || $[42] !== t7 || $[43] !== t8 || $[44] !== t9) {
    t13 = <div className="p-2">{t7}{t8}{t9}{t10}{t11}{t12}</div>;
    $[40] = t11;
    $[41] = t12;
    $[42] = t7;
    $[43] = t8;
    $[44] = t9;
    $[45] = t13;
  } else {
    t13 = $[45];
  }
  return t13;
}